import '../styles/bootstrap/index.scss';
import 'src/polyfills/replaceAll';
import 'src/polyfills/at';
import 'src/utils/yup';
import App, {AppContext, AppProps} from 'next/app';
import {Layout} from '@pageComponents/Layout/Layout';
import {getIntlProps, withIntlApp} from '@moxy/next-intl';
import Head from 'next/head';
import {shouldPolyfill as shouldPolyfillCanonicalLocales} from '@formatjs/intl-getcanonicallocales/should-polyfill';
import {shouldPolyfill as shouldPolyfillLocale} from '@formatjs/intl-locale/should-polyfill';
import {shouldPolyfill as shouldPolyfillPluralRules} from '@formatjs/intl-pluralrules/should-polyfill';
import {shouldPolyfill as shouldPolyfillNumberFormat} from '@formatjs/intl-numberformat/should-polyfill';
import {SessionProvider} from 'next-auth/react';
import {AuthorizedApolloClient} from '@utils/auth';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import {LocaleType} from 'src/types/general';
import SSRProvider from 'react-bootstrap/SSRProvider';
import {Spinner} from '@components/Spinner';
import {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import {ToastProvider} from '@utils/toast';
import {useIntl} from 'react-intl';
import {dataLayerPushPageView} from "@utils/gtm";

const MyApp = ({Component, pageProps}: AppProps) => {
    const router = useRouter();
    const intl = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [lastUrl, setLastUrl] = useState<string>();

    useEffect(() => {
        //dataLayerPushPageView();
        const handleStart = (url: string) => {
            setLoading(true);
        };
        const handleComplete = (url: string) => {
            dataLayerPushPageView(lastUrl, pageProps.dlObject);
            setLoading(false);
            setLastUrl(window.location.href);
        };

        router.events.on('routeChangeStart', handleStart);
        router.events.on('routeChangeComplete', handleComplete);
        router.events.on('routeChangeError', handleComplete);

        return () => {
            router.events.off('routeChangeStart', handleStart);
            router.events.off('routeChangeComplete', handleComplete);
            router.events.off('routeChangeError', handleComplete);
        };
    }, [router.events, pageProps.dlObject, lastUrl]);

    useEffect(() => {
        if (typeof window !== 'undefined' && 'serviceWorker' in navigator && window.workbox !== undefined) {
            const wb = window.workbox;

            // A common UX pattern for progressive web apps is to show a banner when a service worker has updated and waiting to install.
            // NOTE: MUST set skipWaiting to false in next.config.js pwa object
            // https://developers.google.com/web/tools/workbox/guides/advanced-recipes#offer_a_page_reload_for_users
            const promptNewVersionAvailable = (event: any) => {
                // `event.wasWaitingBeforeRegister` will be false if this is the first time the updated service worker is waiting.
                // When `event.wasWaitingBeforeRegister` is true, a previously updated service worker is still waiting.
                // You may want to customize the UI prompt accordingly.
                if (
                    confirm(
                        intl.formatMessage({defaultMessage: 'Novější verze aplikace je k dispozici. Chcete znovu načíst pro aktualizaci?'}),
                    )
                ) {
                    wb.addEventListener('controlling', (event: any) => {
                        window.location.reload();
                    });

                    // Send a message to the waiting service worker, instructing it to activate.
                    wb.messageSkipWaiting();
                } else {
                    console.log(
                        'User rejected to reload the web app, keep using old version. New version will be automatically load when user open the app next time.',
                    );
                }
            };

            wb.addEventListener('waiting', promptNewVersionAvailable);
        }
    }, [intl]);

    return (
        <SSRProvider>
            <SessionProvider
                session={pageProps.session}
                // Re-fetch session every 10 minutes
                refetchInterval={10 * 60}
                // Re-fetches session when window is focused
                refetchOnWindowFocus={true}
            >
                <GoogleReCaptchaProvider
                    reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
                    language={pageProps.locale}
                    scriptProps={{
                        async: true,
                    }}
                >
                    <AuthorizedApolloClient initialCache={pageProps.apolloCache}>
                        <ToastProvider>
                            <Layout withFooter={pageProps.withFooter} withHeaderBorder={pageProps.withHeaderBorder}>
                                <Head>
                                    <meta
                                        name="viewport"
                                        content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
                                    />
                                    <meta
                                        name={'keywords'}
                                        key={"keywords"}
                                        content={intl.formatMessage({
                                            defaultMessage:
                                                'Reality, Nemovitosti, prodej bez realitky, pronájem bez realitky, bez realitní kanceláře, prodej bytů, pronájem bytů, prodej domů, pronájem domů, prodej pozemků',
                                        })}
                                    />

                                    {typeof pageProps.withChatBot !== "undefined" && !pageProps.withChatBot &&
                                    <style key={"chatbot"}>{`.ew-button-container {display: none;}`}</style>
                                    }
                                </Head>
                                <Component {...pageProps} />
                                {loading && <Spinner animation={'border'} fullPage overlay />}
                            </Layout>
                        </ToastProvider>
                    </AuthorizedApolloClient>
                </GoogleReCaptchaProvider>
            </SessionProvider>
        </SSRProvider>
    );
};

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
MyApp.getInitialProps = async (appCtx: AppContext) => {
    const [intlProps, appProps] = await Promise.all([
        getIntlProps(appCtx.router.locale ?? appCtx.router.defaultLocale ?? 'cs'),
        App.getInitialProps(appCtx),
    ]);

    return {
        ...intlProps,
        ...appProps,
    };
};

const loadLocale = async (locale: LocaleType) => {
    await polyfill(locale);

    const module = await import(/* webpackChunkName: "intl-messages" */ `src/translations/compiled/${locale}.json`);

    return module.default;
};

async function polyfill(locale: LocaleType) {
    const imports = [];
    if (shouldPolyfillCanonicalLocales()) {
        imports.push(import('@formatjs/intl-getcanonicallocales/polyfill'));
    }

    if (shouldPolyfillLocale()) {
        imports.push(import('@formatjs/intl-locale/polyfill'));
    }

    const unsupportedPluralLocale = shouldPolyfillPluralRules(locale);
    if (unsupportedPluralLocale) {
        imports.push(
            import('@formatjs/intl-pluralrules/polyfill-force').then(
                () => import(`@formatjs/intl-pluralrules/locale-data/${unsupportedPluralLocale}`),
            ),
        );
    }

    const unsupportedNumberFormatLocale = shouldPolyfillNumberFormat(locale);
    // This locale is supported
    if (unsupportedNumberFormatLocale) {
        // Load the polyfill 1st BEFORE loading data
        imports.push(
            import('@formatjs/intl-numberformat/polyfill-force').then(
                () => import(`@formatjs/intl-numberformat/locale-data/${unsupportedNumberFormatLocale}`),
            ),
        );
    }

    await Promise.all(imports);
}

export default withIntlApp(loadLocale)(MyApp);
